import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users, ClipboardList, CreditCard, Bot, BarChart3,
  LifeBuoy, Bell, LogOut, ShieldCheck, Loader2, Phone, PhoneCall, ScrollText, Megaphone, Search, Mail, Webhook,
} from "lucide-react";
import { useAdminAuth } from "@/context/AdminAuthContext";
import { adminApi } from "@/lib/adminApi";
import AdminGlobalSearch from "@/components/admin/AdminGlobalSearch";

const NAV = [
  { to: "/admin", key: "overview", label: "Overview", icon: LayoutDashboard, roles: ["super_admin", "admin", "support", "sales", "operations", "read_only"], end: true },
  { to: "/admin/customers", key: "customers", label: "Customers", icon: Users, roles: ["super_admin", "admin", "support", "sales", "operations", "read_only"] },
  { to: "/admin/subscriptions", key: "subscriptions", label: "Subscriptions", icon: CreditCard, roles: ["super_admin", "admin", "sales"] },
  { to: "/admin/phone-numbers", key: "phone-numbers", label: "Phone Numbers", icon: Phone, roles: ["super_admin", "admin", "operations"] },
  { to: "/admin/twilio-numbers", key: "twilio-numbers", label: "Twilio Numbers", icon: PhoneCall, roles: ["super_admin", "admin", "operations"] },
  { to: "/admin/square-webhook", key: "square-webhook", label: "Square Webhook", icon: Webhook, roles: ["super_admin", "admin"] },
  { to: "/admin/dfy", key: "dfy", label: "Done-For-You Queue", icon: ClipboardList, roles: ["super_admin", "admin", "operations"] },
  { to: "/admin/ai", key: "ai", label: "AI Management", icon: Bot, roles: ["super_admin", "admin", "operations"] },
  { to: "/admin/analytics", key: "analytics", label: "Analytics", icon: BarChart3, roles: ["super_admin", "admin", "sales", "read_only"] },
  { to: "/admin/announcements", key: "announcements", label: "Announcements", icon: Megaphone, roles: ["super_admin", "admin"] },
  { to: "/admin/email", key: "email", label: "Email", icon: Mail, roles: ["super_admin", "admin"] },
  { to: "/admin/support", key: "support", label: "Support Centre", icon: LifeBuoy, roles: ["super_admin", "admin", "support"] },
  { to: "/admin/audit", key: "audit", label: "Audit Logs", icon: ScrollText, roles: ["super_admin", "admin", "read_only"] },
];

const ROLE_LABEL = { super_admin: "Super Admin", admin: "Admin", support: "Support", sales: "Sales", operations: "Operations", read_only: "Read Only" };

export default function AdminLayout() {
  const { admin, logout } = useAdminAuth();
  const navigate = useNavigate();
  const [notifs, setNotifs] = useState({ events: [], unread: 0 });
  const [open, setOpen] = useState(false);

  const loadNotifs = () => adminApi.get("/admin/notifications").then((r) => setNotifs(r.data)).catch(() => {});
  useEffect(() => { loadNotifs(); const t = setInterval(loadNotifs, 30000); return () => clearInterval(t); }, []);

  const markRead = async () => { await adminApi.post("/admin/notifications/read"); loadNotifs(); };
  const role = admin?.admin_role;
  const items = NAV.filter((n) => n.roles.includes(role));

  return (
    <div className="min-h-screen bg-slate-100 flex">
      {/* Sidebar */}
      <aside className="w-60 bg-slate-950 text-slate-300 flex flex-col fixed inset-y-0 left-0">
        <div className="px-5 py-5 border-b border-slate-800/70 flex items-center gap-2">
          <span className="font-heading text-lg font-extrabold text-white">Opera<span className="text-blue-500">Mind</span></span>
          <span className="text-[9px] font-bold uppercase tracking-widest text-slate-400 border border-slate-700 rounded px-1 py-0.5">Admin</span>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {items.map((n) => (
            <NavLink key={n.key} to={n.to} end={n.end} data-testid={`admin-nav-${n.key}`}
              className={({ isActive }) => `flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${isActive ? "bg-blue-600 text-white" : "text-slate-400 hover:bg-slate-800 hover:text-white"}`}>
              <n.icon className="h-4 w-4" /> {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="px-4 py-4 border-t border-slate-800/70">
          <div className="flex items-center gap-2 mb-3">
            <div className="h-8 w-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-xs font-bold">{(admin?.name || "A")[0]}</div>
            <div className="min-w-0">
              <p className="text-sm text-white truncate">{admin?.name}</p>
              <p className="text-[11px] text-slate-500 flex items-center gap-1"><ShieldCheck className="h-3 w-3" /> {ROLE_LABEL[role] || role}</p>
            </div>
          </div>
          <button data-testid="admin-logout" onClick={() => { logout(); navigate("/admin/login"); }} className="flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors">
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 ml-60">
        <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-6 gap-4 sticky top-0 z-20">
          <AdminGlobalSearch />
          <div className="relative">
            <button data-testid="admin-notif-bell" onClick={() => { setOpen(!open); if (!open && notifs.unread) markRead(); }} className="relative h-9 w-9 rounded-lg hover:bg-slate-100 flex items-center justify-center">
              <Bell className="h-5 w-5 text-slate-600" />
              {notifs.unread > 0 && <span data-testid="admin-notif-count" className="absolute -top-1 -right-1 h-4 min-w-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">{notifs.unread}</span>}
            </button>
            {open && (
              <div className="absolute right-0 mt-2 w-80 bg-white border border-slate-200 rounded-xl shadow-xl overflow-hidden z-30">
                <div className="px-4 py-3 border-b border-slate-100 text-sm font-semibold">Notifications</div>
                <div className="max-h-96 overflow-y-auto divide-y divide-slate-50">
                  {notifs.events.length === 0 && <p className="px-4 py-6 text-sm text-slate-400 text-center">No notifications yet</p>}
                  {notifs.events.map((e) => (
                    <div key={e.id} className="px-4 py-3">
                      <p className="text-sm text-slate-800">{e.title}</p>
                      <p className="text-xs text-slate-400 mt-0.5">{new Date(e.created_at).toLocaleString()}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </header>
        <main className="p-6 max-w-7xl mx-auto"><Outlet /></main>
      </div>
    </div>
  );
}

export function AdminProtected({ children }) {
  const { admin, loading } = useAdminAuth();
  const navigate = useNavigate();
  useEffect(() => { if (!loading && !admin) navigate("/admin/login"); }, [loading, admin, navigate]);
  if (loading || admin === null) return <div className="min-h-screen bg-slate-100 flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  if (!admin) return null;
  return children;
}
